import { useState, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { variants, transition } from './variants';
import FileInput from '../../../FileInput';
import FilesList from '../../../FilesList';
import { useFileUpload } from '../../../../utils/hooks/useFileUpload';
import { useAuth } from '../../../../utils/Auth';
import { generateId } from '../../../../lib/generateId';
import cloneDeep from 'lodash.clonedeep';
import styles from './SelectOption.module.css';

const defaultResource = {
    id: '',
    name: '',
    path: '',
    url: '',
};

export default function SelectOption({ schedule }) {
    const { fileHandler, dataDispatch } = useAuth();
    const { files, setFiles } = fileHandler;

    const [ error, setError ] = useState({ hasError: false, message: '' });

    // cleaner access
    const resources = useMemo(() => schedule.resources, [schedule.resources]);

    const activeResource = useMemo(() => {
        if (resources?.data?.length > 0) {
            return resources.data[0];
        };
        return cloneDeep(defaultResource);
    }, [resources]);

    const options = useMemo(() => ([{
        type: 'link',
        header: 'Link'
    },
    {
        type: 'pdf',
        header: 'PDF'
    },
    {
        type: 'asset',
        header: 'Liquid Label'
    }]), []);

    const acceptedFileTypes = useMemo(() => {
        switch (resources?.type) {
            case 'pdf':
                return ['application/pdf'];
            case 'asset':
                return ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif', 'application/pdf'];
            case 'link':
            default:
                return [];
        };
    }, [resources?.type]);

    const fileHelpers = useFileUpload({
        acceptableFileTypes: acceptedFileTypes,
        type: resources?.type,
        fileHandleType: 'schedule',
        scheduleId: schedule.id,
    });

    const { handleFileSelected } = fileHelpers;

    const handleSelectResourceType = (e) => {
        setError({ hasError: false, message: '' });
        const newType = e.target.value || '';

        if (!newType) {
            setError({ hasError: true, message: 'Invalid schedule option selected.' });
            return;
        };

        let newResourceData = [];

        if (newType === 'link') {
            const resourceObj = {
                ...cloneDeep(defaultResource),
                id: generateId()
            };
            newResourceData = [resourceObj];
        }

        const newResourcesDispatch = {
            data: newResourceData,
            type: newType
        }

        dataDispatch({ 
            type: 'schedule', 
            data: { 
                id: schedule.id,
                resources: {
                    data: newResourceData,
                    type: newType
                }
            }
        });
    };

    // update url input (only for 'link' type)
    const handleRedirect = (e) => {
        const url = e.target.value || '';

        const updatedResourcesData = resources?.data?.map((r,index) => {
            // only update the first resource item for the link type
            if (index === 0) {
                return { ...r, url: url };
            }
            return r;
        }) || [];

        dataDispatch({ 
            type: 'schedule', 
            data: { 
                id: schedule.id, 
                resources: {
                    ...resources,
                    data: updatedResourcesData
                } 
            }
        });
    };

    // renders input type based on selected resource type
    const renderInputType = () => {
        if (resources.type === 'link') {
            return (
                <div className={styles.inputGroup}>
                    <input
                        type='url'
                        id='redirect_url-label'
                        className={styles.input}
                        onChange={handleRedirect}
                        value={activeResource.url}
                    />
                    <label htmlFor='redirect_url-label' className={styles.label}>Link (URL)</label>
                </div>
            );
        }
        
        // File types 'pdf' or 'asset'
        const maxFiles = resources.type === 'pdf' ? 1 : 5;
        
        return (
            <div className={styles.fileContainer}>
                {resources.data.length > 0 && 
                    <FilesList
                        fileHelpers={fileHelpers}
                    />
                }
                <FileInput
                    onFileSelect={handleFileSelected}
                    acceptedFileTypes={acceptedFileTypes}
                    multiple={resources.type !== 'pdf'}
                    header=''
                    label={`Choose ${resources.type === 'pdf' ? 'PDF File' : 'Asset(s)'}`}
                    id='schedule-file-input'
                    name='schedule-file-input'
                    disabled={resources.data.length >= maxFiles}
                />
            </div>
        );
    };

    return (
        <div className={styles.container} >
            <div className={styles.inputGroup}>
                <p className={styles.selectLabel}>Choose Scan Destination</p>
                <select 
                    id='schedule-option-select' 
                    value={resources.type || ''} 
                    onChange={handleSelectResourceType} 
                    className={styles.select}
                >
                    <option default disabled value='' className={styles.option}>Select Type</option>
                    {options.map((opt) => (
                        <option key={opt.type} value={opt.type} className={styles.option} >{opt.header}</option>
                    ))}
                </select>
            </div>
            {(error.hasError && error.message) &&
                <div className={styles.error}>{error.message}</div>
            }
            <AnimatePresence initial={false} >
                {resources.type &&
                    <motion.div
                        key='device'
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                        className={styles.addOptionContainer}
                    >
                        {renderInputType()}
                    </motion.div>
                }
            </AnimatePresence>
        </div>
    );
};